import { useEffect, useRef, useCallback } from 'react'
import { useSellerQueueContext } from '../contexts/SellerQueueContext'
import { getSocketIOExports, checkSocketIOCompatibility } from '../utils/socket-compatibility'
import { useSeller } from './useSeller'

export interface SellerQueueRealtimeEvent {
  type: 'position-updated' | 'status-changed' | 'called'
  data: any
  timestamp: Date
}

/**
 * Hook: useSellerQueueRealtime
 * Manages WebSocket connection for real-time seller queue updates
 * Uses existing socket-compatibility layer to avoid breaking existing functionality
 * Handles fallback to polling if websocket unavailable
 */
export const useSellerQueueRealtime = (onEvent?: (event: SellerQueueRealtimeEvent) => void) => {
  const socketRef = useRef<any>(null)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const onEventRef = useRef(onEvent)

  const { setIsOnline } = useSellerQueueContext()
  const { getTokenSeller } = useSeller()

  const tokenSeller = getTokenSeller()
  const sellerId = tokenSeller?.id
  const pluginspaceId = tokenSeller?.pluginspaceId
  const token = tokenSeller ? `${tokenSeller.id}:${tokenSeller.pluginspaceId}` : null

  useEffect(() => {
    onEventRef.current = onEvent
  }, [onEvent])

  /**
   * Initialize WebSocket connection
   */
  const initializeSocket = useCallback(() => {
    // Check compatibility first
    const compat = checkSocketIOCompatibility()
    if (!compat.compatible) {
      console.warn('[useSellerQueueRealtime] Socket.io not compatible, using polling fallback')
      setIsOnline(false)
      return false
    }

    try {
      const { io } = getSocketIOExports()
      const SERVICE_URL =
        process.env.REACT_APP_QUEUE_SERVICE_URL ||
        (process.env.REACT_APP_API_URL || 'http://localhost:7004').replace(/\/api\/?$/, '')

      if (socketRef.current) {
        socketRef.current.off()
        socketRef.current.disconnect()
        socketRef.current = null
      }

      socketRef.current = io(SERVICE_URL, {
        auth: {
          token,
          sellerId,
          pluginspaceId,
        },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
        transports: ['websocket', 'polling'],
      })

      // Connection established
      socketRef.current.on('connect', () => {
        console.log('[useSellerQueueRealtime] Connected to queue service')
        setIsOnline(true)
      })

      const emitEvent = (type: SellerQueueRealtimeEvent['type'], data: any) => {
        const event: SellerQueueRealtimeEvent = {
          type,
          data,
          timestamp: new Date(),
        }
        onEventRef.current?.(event)
      }

      socketRef.current.on('seller-queue:called', (data: any) => {
        console.log('[useSellerQueueRealtime] seller-queue:called', data)
        emitEvent('called', data)
      })

      socketRef.current.on('seller-queue:position-updated', (data: any) => {
        console.log('[useSellerQueueRealtime] seller-queue:position-updated', data)
        emitEvent('position-updated', data)
      })

      socketRef.current.on('seller-queue:status-changed', (data: any) => {
        console.log('[useSellerQueueRealtime] seller-queue:status-changed', data)
        emitEvent('status-changed', data)
      })

      // Connection error
      socketRef.current.on('connect_error', (error: any) => {
        console.warn('[useSellerQueueRealtime] Connection error:', error)
        setIsOnline(false)
      })

      // Disconnected
      socketRef.current.on('disconnect', (reason: string) => {
        console.log('[useSellerQueueRealtime] Disconnected:', reason)
        setIsOnline(false)
      })

      return true
    } catch (error) {
      console.error('[useSellerQueueRealtime] Failed to initialize socket:', error)
      setIsOnline(false)
      return false
    }
  }, [token, sellerId, pluginspaceId, setIsOnline, onEvent])

  /**
   * Cleanup socket connection
   */
  const cleanup = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.off()
      socketRef.current.disconnect()
      socketRef.current = null
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }, [])

  /**
   * Initialize on mount
   */
  useEffect(() => {
    if (!sellerId || !pluginspaceId) return

    const socketConnected = initializeSocket()

    // If socket failed, setup polling fallback every 30 seconds
    if (!socketConnected && !pollIntervalRef.current) {
      console.log('[useSellerQueueRealtime] Setting up polling fallback (30s interval)')
      pollIntervalRef.current = setInterval(() => {
        // This is where we'd emit a manual check event
        // Components that need updates will use refetch functions
      }, 30000)
    }

    return () => cleanup()
  }, [sellerId, pluginspaceId, initializeSocket, cleanup])

  return {
    isConnected: Boolean(socketRef.current?.connected),
    disconnect: cleanup,
  }
}
